import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { logoStyles } from "@insite/content-library/Widgets/Basic/Logo";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerWrapper?: InjectableCss;
    scrollFixed?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 50px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    headerWrapper: {
        css: css`
            padding: 0 !important;
            max-width: none !important;
            height: 100%;
            @media all and (max-width: 991px) and (min-width: 768px) {
                position: relative;
            }
            .sticky {
                top: 0;
                left: 0;
                padding: 0 !important;
                max-width: none !important;
                width: 100%;
                height: auto;
                z-index: 999;
                background: #fff;
                border-bottom: 3px solid #006bc0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                position: absolute;
                                border-bottom: none;
                            `,
                            css`
                                position: absolute;
                                border-bottom: none;
                            `,
                            css`
                                position: absolute;
                                border-bottom: none;
                            `,
                            css`
                                position: fixed;
                                border-bottom: 3px solid #006bc0;
                            `,
                            css`
                                position: fixed;
                                border-bottom: 3px solid #006bc0;
                            `,
                            css`
                                position: fixed;
                                border-bottom: 3px solid #006bc0;
                            `,
                        ],
                        "min",
                    )}
            }
            &.headerClass {
                + main {
                    padding-top: 50px !important;
                }
                .MenuBar {
                    height: 50px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    height: 71px;
                                `,
                                css`
                                    height: 71px;
                                `,
                                css`
                                    height: 71px;
                                `,
                                css`
                                    height: 50px;
                                `,
                                css`
                                    height: 50px;
                                `,
                            ],
                            "min",
                        )}
                }
                #search_zone {
                    margin-top: 4px !important;
                    display: block;
                    align-items: center;
                    justify-content: right;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    display: none;
                                `,
                                css`
                                    display: none;
                                `,
                                css`
                                    display: none;
                                `,
                                css`
                                    display: block;
                                `,
                                css`
                                    display: block;
                                `,
                                css`
                                    display: block;
                                `,
                            ],
                            "min",
                        )}
                }
                .megaMenuDrawerWrapper {
                    top: 50px !important;
                    max-height: 500px;
                    overflow: auto;
                }
            }
        `,
    },
    scrollFixed: {
        css: css`
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            padding: 0 !important;
            max-width: none !important;
            width: 100%;
            height: auto;
            z-index: 99;
            background: #fff;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            position: fixed;
                        `,
                        null,
                    ],
                    "max",
                )}

            * {
                transition: all 0.9s ease;
            }
            > div[class^="Wrapper-sc"] {
                > div[class^="HiddenStyle-sc"] {
                    > div[class^="Navigation-sc"] {
                        display: none;
                    }
                }
            }
        `,
    },
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const [offset1, setOffset1] = useState(0);

    const addStyleRel = React.useCallback(() => {
        const updateRelAttr = document.querySelector('link[type="text/css"]');
        updateRelAttr?.setAttribute("rel", "preload stylesheet");
    }, []);

    useEffect(() => {
        const MenuBAr = document.getElementById("MenuBAr");
        const header = document.getElementsByTagName("header")[0];
        const mainbody = document.getElementsByTagName("main")[0];
        const submenubar = document.getElementById("submenubar")?.clientHeight;
        if (MenuBAr && header && window.screen.width > 990) {
            if (window.pageYOffset > 40) {
                MenuBAr.classList.add("sticky");
                header.classList.add("headerClass");
                // mainbody.style.marginTop = "40px !important";
            } else {
                MenuBAr.classList.remove("sticky");
                header.classList.remove("headerClass");
                // mainbody.style.marginTop = "0px !important";
            }
        }
    }, [offset1]);
    useEffect(() => {
        const handleScroll = () => {
            const check = window.parent.document.getElementById("siteIFrame");
            if (!check) {
                setOffset1(window.pageYOffset);
            } else {
                setOffset1(2000);
            }
        };
        addStyleRel();

        window.addEventListener("scroll", handleScroll);
        () => window.removeEventListener("scroll", handleScroll);
    }, []);
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    // Five9 Chat Widget show and hide on the basis of language change
    useEffect(() => {
        if (session?.language?.languageCode === "en") {
            const five9Btn = document.querySelector(".five9-frame");
            five9Btn?.classList.remove("five9-display-none");
            five9Btn?.classList.add("five9-display-block");
        } else {
            const five9Btn = document.querySelector(".five9-frame");
            five9Btn?.classList.remove("five9-display-block");
            five9Btn?.classList.add("five9-display-none");
        }
    }, [session]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page
                as="header"
                isHeader={true}
                fullWidth={[true, true, false, false, false]}
                {...styles.headerWrapper}
                // {...(offset1 > 130 ? { ...styles.scrollFixed } : "")}
            >
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <Zone contentId={id} zoneName="SecondaryNavigation" />

                    {/* <StyledWrapper {...styles.container}>
                    <Zone contentId={id} zoneName="MainNavigation" />
                    <Zone contentId={id} zoneName="Cart" />
                </StyledWrapper> */}
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
